import { useApolloClient, useLazyQuery } from '@apollo/react-hooks'
import PortalProgram from '@classes/Program'
import PortalPrograms from '@classes/Programs'
import PortalRegistrations from '@classes/Registrations'
import PortalStudentProfile from '@classes/StudentProfile'
import { GetDecrypts } from '@interfaces/Decrypt'
import { StoreProps } from '@interfaces/StoreState'
import { GetUserProfile, UserProfileProps } from '@interfaces/UserProfile'
import { GET_DECRYPTS_WITH_TYPE } from '@queries/decrypt'
import { GET_USER_PROFILE } from '@queries/userProfile'
import { SF_STANDARD_ROLE } from '@utils/constants'
import { ApolloError, ApolloQueryResult, OperationVariables } from 'apollo-client'
import { isEmpty } from 'lodash'
import { useRouter } from 'next/router'
import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'

export type TRouteParams = 'instructorId' | 'programId' | 'sessionId' | 'sid' | 'path'

export interface FMIStudentProfileProvider {
  children?: React.ReactNode
  studentId?: string
  isAdmin?: boolean
  isBulkPage?: boolean
}

export interface IFMIRouteParamObj {
  [key: string]: string
}

export type TGetRouteParams = (excludes?: TRouteParams[]) => string

export interface IFMIStudentProfileContext {
  isAdmin: boolean
  adminCustomerId: string
  isSessionAdmin: boolean
  userProfileLoading: boolean
  userProfileError?: ApolloError
  userProfile: UserProfileProps
  activeMaterial?: string | null
  StudentPrograms: PortalPrograms
  EventRecordId: string
  IsRegGroupProgram: boolean
  currStudent: PortalStudentProfile
  currProgram: PortalProgram
  baseRoute: string
  getRouteParams: TGetRouteParams
  refetchUserProfile: (variables?: OperationVariables | undefined) => Promise<ApolloQueryResult<GetUserProfile>>
  isBulkPage?: boolean
}

export const FMIStudentProfileContext = React.createContext<IFMIStudentProfileContext>({} as IFMIStudentProfileContext)

export const FMIStudentProfileProvider: React.FC<FMIStudentProfileProvider> = ({
  isAdmin = false,
  studentId = '',
  children,
  isBulkPage = false,
}) => {
  const client = useApolloClient()
  const router = useRouter()
  const [getStudentProfile, { loading: userProfileLoading, error: userProfileError, data, refetch }] =
    useLazyQuery<GetUserProfile>(GET_USER_PROFILE)
  const { profile, activeMaterial, session } = useSelector(({ userProfile, dashboard, session }: StoreProps) => ({
    profile: userProfile.info,
    activeMaterial: dashboard.activeMaterial,
    session,
  }))
  const userProfile = useMemo(() => {
    if (!isEmpty(profile.StudentProfile)) return profile

    return data?.getUserProfile || undefined
  }, [data?.getUserProfile, profile])!
  const { StudentPrograms, EventRecordId, IsRegGroupProgram, currStudent, currProgram } = useMemo(() => {
    const Registrations = new PortalRegistrations(userProfile?.StudentProfile?.StudentProfileInfo?.RegistrationWrappers)
    const currStudent = new PortalStudentProfile(userProfile?.StudentProfile?.StudentProfileInfo!)

    return {
      StudentPrograms: new PortalPrograms(
        userProfile?.StudentProfile?.StudentProfileInfo?.ProgramStudentWrappers ?? []
      ),
      EventRecordId: Registrations.FirstItem?.RegistrationId,
      IsRegGroupProgram: Registrations.FirstItem?.registration?.Programs.some(
        ({ ProgramId, IsGroup }) => ProgramId === router.query.programId && IsGroup
      )!,
      currStudent,
      currProgram: currStudent.Programs[router.query.programId as string],
    }
  }, [userProfile])
  const baseRoute = useMemo(() => {
    if (!isAdmin) return '/scheduling/instructors'

    return session.cognitoGroups?.includes(SF_STANDARD_ROLE) ? '/sfstandard/instructors' : '/salesforce/instructors'
  }, [session.cognitoGroups])
  const adminCustomerId = useMemo(
    () => session?.user?.signInUserSession?.idToken?.payload?.['custom:customerId'] || '',
    [session]
  )

  const fetchProfile = async () => {
    let decrypts: string[] = []
    if (isAdmin) {
      const decrypt = await client.query<GetDecrypts>({
        query: GET_DECRYPTS_WITH_TYPE,
        variables: {
          encrypts: decodeURIComponent(studentId.split('%20').join('+').split(' ').join('+')),
          type: 'Student',
        },
      })

      decrypts = decrypt?.data?.decrypt?.decrypts
    }

    const Id = decrypts.length && isAdmin ? decrypts?.[0] : studentId
    getStudentProfile({
      variables: {
        Id,
        Type: 'Student',
      },
    })
  }

  useEffect(() => {
    if (studentId && isEmpty(userProfile?.StudentProfile)) {
      fetchProfile()
    }
  }, [isAdmin, studentId, userProfile])

  const getRouteParams: TGetRouteParams = (excludes = []) =>
    (Object.keys(router.query || {}) as TRouteParams[])
      .filter((key) => !excludes.includes(key))
      .map((key) => (key ? `${key}=${router.query[key]}` : null))
      .filter((r) => r)
      .join('&')

  return (
    <FMIStudentProfileContext.Provider
      value={{
        isAdmin,
        adminCustomerId,
        isSessionAdmin: session?.isAdmin!,
        userProfileLoading,
        userProfileError,
        userProfile,
        activeMaterial,
        StudentPrograms,
        EventRecordId,
        IsRegGroupProgram,
        currStudent,
        currProgram,
        baseRoute,
        getRouteParams,
        refetchUserProfile: refetch,
        isBulkPage,
      }}
    >
      {children}
    </FMIStudentProfileContext.Provider>
  )
}

export default FMIStudentProfileProvider
